import { cn } from "@/libs/utils";
import React from "react";
import Image from "next/image";
import Link from "next/link";
import { useTrans } from "@/utils/useTrans";
import { LazyLoadVideo } from "@/v2-components/lazy-load-video";
import { Button } from "@/v2-components/ui/button";

export function GridsCard({
  className,
  children,
}: React.PropsWithChildren<{
  className?: string;
}>) {
  return (
    <div className="w-full">
      <div
        className={cn(
          "mx-auto grid w-full max-w-screen-checkout-max gap-x-6 gap-y-3 laptop:auto-cols-fr laptop:grid-flow-col",
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}

export const FeatureGrids = ({
  imageSrc,
  videoSrc,
  title,
  description,
  className,
  imageClassName,
  reverse = false,
  cta,
  children,
}: React.PropsWithChildren<{
  imageSrc?: string;
  videoSrc?: string;
  title?: string;
  description?: string;
  className?: string;
  imageClassName?: string;
  reverse?: boolean;
  /** 설명 아래 CTA. `label` 은 번역 키(이 컴포넌트가 `tactsuit` ns 로 `t()` 한다). */
  cta?: { label: string; href: string };
}>) => {
  const { t } = useTrans(["tactsuit"]);
  if (children) {
    return <GridsCard className={className}>{children}</GridsCard>;
  }

  const featureElement = (
    <div
      className={cn(
        "relative aspect-square overflow-hidden rounded-2xl",
        // `reverse` 는 **데스크탑 좌우 배치만** 뜻한다 — 모바일은 어느 쪽이든
        // 미디어가 먼저다(시안 `28718:167817` MOLLE 이 미디어 위·텍스트 아래).
        // 그래서 DOM 순서는 미디어 고정이고 `laptop:` 에서만 뒤로 민다.
        // 형제 구현(`components/tactsuit/sleeve-feature-sections.tsx:48`)이 쓰는 표기와 같다.
        reverse && "laptop:order-1",
        imageClassName,
      )}
    >
      {imageSrc ? (
        <Image src={imageSrc} fill alt="" />
      ) : videoSrc ? (
        <LazyLoadVideo
          src={videoSrc}
          className="absolute size-full object-cover"
          autoPlay
          loop
          muted
          playsInline
        />
      ) : null}
    </div>
  );

  const textElement = (
    <TextCard className="justify-start laptop:justify-center">
      <div className="flex flex-col laptop:max-w-screen-xs">
        {/* 제목↔설명 간격은 시안 그대로 모바일 12 · 데스크탑 20 이다.
            CTA 는 이 묶음 **밖**에 둔다 — 시안에서 설명↔버튼 간격이 두 폭 다 20 이라
            (모바일 `28718:167820`: 텍스트 묶음 0..130 · Button y=150,
             데스크탑 `28716:154318`: 설명 끝 273 · Button y=293)
            묶음 안에 넣으면 모바일에서 12 가 된다. */}
        <div className="flex flex-col gap-y-3 laptop:gap-y-5">
          {!!title && <Title>{t(title)}</Title>}
          {!!description && <Description>{t(description)}</Description>}
        </div>
        {!!cta && (
          <Button
            asChild
            size="sm"
            shape="pill"
            variant="blue-fill"
            // `size="sm"` = min-h-40 + text-sm(12/16) · `blue-fill` = bg-blue-60(#0F62FE)
            // · `pill` = rounded-full — 셋 다 시안값과 일치한다(2026-09-14 실측).
            // `px-6`(24) 는 `size="sm"` 의 `px-2` 를 덮는다. 시안은 데스크탑 좌측 정렬 ·
            // 모바일 중앙(x=98.5 = (328-131)/2) 이라 `self-center laptop:self-start`.
            className="mt-5 self-center px-6 tracking-[0.1px] laptop:self-start"
          >
            <Link href={cta.href}>{t(cta.label)}</Link>
          </Button>
        )}
      </div>
    </TextCard>
  );

  // ⚠️ DOM 순서를 `reverse` 로 뒤집지 않는다. 종전엔 뒤집어서 **모바일에서만**
  // 텍스트가 먼저 나왔다(실측 2026-09-14 @390: MOLLE 텍스트 y=5167 < 미디어 y=5309).
  // 시안 모바일은 두 축 다 미디어가 먼저라, 좌우 배치는 위 `laptop:order-1` 이 맡는다.
  return (
    <GridsCard className={className}>
      {featureElement}
      {textElement}
    </GridsCard>
  );
};

const Title = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => (
  <h2 className={cn("text-3xl text-coolgray-90 laptop:text-5xl", className)}>
    {children}
  </h2>
);

const Description = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => (
  <Title
    className={cn(
      "text-base laptop:whitespace-pre-line laptop:text-lg",
      className,
    )}
  >
    {children}
  </Title>
);

const TextCard = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => (
  <div className={cn("flex size-full items-center justify-center", className)}>
    {children}
  </div>
);

export const WirelessFeature = ({
  imageSrc,
  ...rest
}: React.PropsWithChildren<{ imageSrc: string; className?: string }>) => {
  return (
    <FeatureGrids
      imageSrc={imageSrc}
      title="Latency-Free Wireless Support"
      description="Move freely and immerse yourself with devices that are wirelessly connected, ensuring a seamless experience."
      {...rest}
    />
  );
};

export const DirectionalFeedabackFeature = ({
  imageSrc,
  ...rest
}: React.PropsWithChildren<{ imageSrc: string; className?: string }>) => {
  return (
    <FeatureGrids
      imageSrc={imageSrc}
      title="Precise Directional Feedback You Can Feel"
      description="Experience every attack, explosion, and moment with life like accuracy."
      {...rest}
    />
  );
};
